/**
 * The brand this build is, resolved once for the page.
 *
 * `brand.ts` is pure and knows nothing about where a profile comes from, which
 * is what makes it testable against a hand-written one. This is the impure
 * half: it reads the profile the build inlined and holds the answer.
 *
 * ## `__QF_BRAND__` does not exist yet, and that is the point
 *
 * The vite plugin that defines it is a later step. Until then the identifier is
 * simply absent, `resolveBrand(undefined)` returns the registry's defaults, and
 * **the app is exactly what it was** — same name, same labels, same placements.
 * So every surface can be converted to read this now, one at a time, with no
 * visible change and nothing to keep in sync behind a flag.
 *
 * `typeof x !== "undefined"` rather than a bare read, because an identifier
 * that was never declared throws a `ReferenceError` on access and `typeof` is
 * the one operator that does not. When the define lands it is replaced at build
 * time with a literal and this collapses to a constant.
 *
 * ## Read at module load, never re-read
 *
 * A brand cannot change while the app is running: it was decided by the build
 * that produced the bundle. So this is a `const`, not a hook and not a context
 * — a provider would add a re-render boundary around the shell to publish
 * something that cannot change, and every consumer would need to handle a state
 * where the answer is not in yet. There is no such state.
 */

import type { Theme } from "@qufolio/model";

import { resolveBrand, type ResolvedBrand } from "./brand";

declare const __QF_BRAND__: Parameters<typeof resolveBrand>[0];

/**
 * The brand's own theme documents, inlined separately from the profile.
 *
 * Separately because the profile carries *paths* — it is written by hand, and a
 * theme is forty colours — so the build reads each file and puts the documents
 * here. Two defines, each recognisable beside the file it came from.
 */
declare const __QF_BRAND_THEMES__: Theme[];

/** What this app is called, what it has, where it puts it, and how it looks. */
export const BRAND: ResolvedBrand = resolveBrand(
  typeof __QF_BRAND__ !== "undefined" ? __QF_BRAND__ : undefined,
  typeof __QF_BRAND_THEMES__ !== "undefined" ? __QF_BRAND_THEMES__ : [],
);
